import React, {
  FunctionComponent,
  useState,
  useEffect,
  ChangeEvent,
} from "react";
import { TextField, Button, ChoiceList } from "@cmsgov/ds-medicare-gov";
import { County } from "../@types";
import { useTranslate } from "../helpers/intlHooks";
import * as api from "../api";
import { limitInputLength } from "../helpers/objectUtilities";

function countyToString(county: County, zip: string): string {
  return `${zip}, ${county.name}, ${county.state}`;
}

interface CountyPickerProps {
  label: string;
  onCountySelect: (county?: County, zipcode?: string) => void;
  onCountySearch?: (zip: string) => void;
  initialZip?: string;
  initialFips?: string;
  className?: string;
  zipError?: string;
  countyError?: string;
  buttonLabel?: string;
}

export const CountyPicker: FunctionComponent<CountyPickerProps> = ({
  label,
  onCountySelect,
  onCountySearch,
  initialZip,
  initialFips,
  className,
  zipError,
  countyError,
  buttonLabel,
}) => {
  const t = useTranslate();
  const [fetching, setFetching] = useState(false);
  const [zipErrorMessage, setZipErrorMessage] = useState<undefined | string>(
    undefined
  );
  const [countyErrorMessage, setCountyErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [zipcode, setZipcode] = useState("");
  const [countyResults, setCountyResults] = useState<County[]>([]);
  let autocompleteInput: HTMLInputElement;

  useEffect(() => {
    setZipErrorMessage(zipError);
  }, [zipError]);

  useEffect(() => {
    setCountyErrorMessage(countyError);
  }, [countyError]);

  const searchForCounties = async (autofillZip?: string): Promise<void> => {
    setZipErrorMessage(undefined);

    const input = autofillZip || autocompleteInput.value;

    if (autofillZip) {
      autocompleteInput.value = autofillZip;
    }

    if (!autofillZip && onCountySearch) {
      onCountySearch(input);
    }

    if (/^\d{5}$/.test(input)) {
      setZipcode(input);
      setFetching(true);
      setCountyResults([]);
      onCountySelect();
      try {
        const counties = await api.getCounties(input);
        setCountyResults(counties);

        if (autofillZip) {
          const countyMatch = counties.find(
            county => county.fips == initialFips
          );

          if (countyMatch) {
            onCountySelect(countyMatch, autofillZip);
          }
        }

        if (counties.length === 1) {
          onCountySelect(counties[0], input);
        }
      } catch (error) {
        setZipErrorMessage(t("error_message.fetching_county"));
      }
      setFetching(false);
    } else {
      setZipErrorMessage(t("county_picker.error_message"));
    }
  };

  const onCountySelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onCountySelect(JSON.parse(e.target.value), zipcode);
  };

  const onSubmit = (e: ChangeEvent<HTMLFormElement>): void => {
    e.preventDefault();
    searchForCounties();
  };

  useEffect(() => {
    if (initialZip) {
      searchForCounties(initialZip);
    }
  }, []);

  return (
    <div className={`CountyPicker ${className ? className : ""}`}>
      <form
        className="CountyPicker__input e2e-select-county"
        onSubmit={onSubmit}
      >
        <TextField
          label={label}
          labelClassName="questionHeading e2e-enter-zip CountyPicker__label"
          className="ds-c-field--countyPicker"
          onChange={(): void => limitInputLength("countyPicker input", 5)}
          name="zip"
          inputRef={(ref: HTMLInputElement): HTMLInputElement =>
            (autocompleteInput = ref)
          }
          errorMessage={zipErrorMessage}
        />

        <Button
          className="e2eCountyPickerSelectBtn"
          type="submit"
          variation="primary"
        >
          {buttonLabel || t("county_picker.button_text")}
        </Button>
        {fetching}
      </form>
      {fetching && <p>{t("app.loading")}...</p>}
      {!!countyResults.length && (
        <ChoiceList
          name="county"
          label={t("county_picker.select_your_county")}
          type="radio"
          errorMessage={countyErrorMessage}
          choices={countyResults.map(county => {
            return {
              label: countyToString(county, zipcode),
              value: JSON.stringify(county),
              defaultChecked:
                countyResults.length === 1
                  ? true
                  : initialFips
                  ? initialFips === county.fips
                  : false,
            };
          })}
          onChange={onCountySelected}
        />
      )}
    </div>
  );
};

export default CountyPicker;
